/**
 * NicknameInput Component
 * Reusable nickname input field with validation
 */

import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  isValidNicknameFormat,
  MAX_NICKNAME_LENGTH,
  normalizeWhitespace,
} from "@shared/nickname";

interface NicknameInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  placeholder?: string;
  label?: string;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
}

export function NicknameInput({
  value,
  onChange,
  onValidationChange,
  placeholder = "닉네임을 입력하세요",
  label = "닉네임",
  maxLength = MAX_NICKNAME_LENGTH,
  disabled = false,
  className = "",
}: NicknameInputProps) {
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!touched) return;

    const normalized = normalizeWhitespace(value);

    if (!normalized) {
      setError(null);
      onValidationChange?.(false);
      return;
    }

    if (normalized.length > maxLength) {
      setError(`최대 ${maxLength}자까지 입력할 수 있습니다`);
      onValidationChange?.(false);
      return;
    }

    if (!isValidNicknameFormat(normalized)) {
      setError("지역 + 형용사 + 호주 동물 형식으로 입력해주세요");
      onValidationChange?.(false);
      return;
    }

    setError(null);
    onValidationChange?.(true);
  }, [value, touched, maxLength, onValidationChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (!touched) {
      setTouched(true);
    }
  };

  const handleBlur = () => {
    setTouched(true);
  };

  const characterCount = value.length;
  const remaining = maxLength - characterCount;

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <Label htmlFor="nickname-input">{label}</Label>
        <span
          className={`text-xs ${
            remaining < 0
              ? "text-destructive"
              : remaining < 3
              ? "text-orange-500"
              : "text-muted-foreground"
          }`}
        >
          {characterCount}/{maxLength}자
        </span>
      </div>
      <Input
        id="nickname-input"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={maxLength + 10} // Allow some overflow for validation
        disabled={disabled}
        className={error ? "border-destructive" : ""}
      />
      {error && touched && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      {!error && touched && value && (
        <p className="text-sm text-muted-foreground">
          사용 가능한 닉네임입니다
        </p>
      )}
    </div>
  );
}
